'use client'

import React, { useState } from 'react'
import { useTagStore } from '@/lib/useTagStore'
import { motion } from 'framer-motion'
import { formatDate } from 'pliny/utils/formatDate.js'
import { type CoreContent } from 'pliny/utils/contentlayer.js'
import type { Blog } from 'contentlayer/generated'
import Link from '@/components/mdxcomponents/link'
import { sortByDate } from '@/lib/sortByDate'
import Pagination from './Pagination'
import tagData from '@/app/[locale]/blog/tag-data.json'
import { POSTS_PER_PAGE } from '@/data/blogs/postsPerPage'
import { useTranslations } from 'next-intl'
import { type Locale } from '@/navigation'
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"

interface PaginationProps {
  totalPages: number
  currentPage: number
  params: { locale: Locale }
}

interface ListLayoutProps {
  params: { locale: Locale }
  posts: CoreContent<Blog>[]
  title: string
  initialDisplayPosts?: CoreContent<Blog>[]
  pagination?: PaginationProps
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
}

const item = {
  hidden: { opacity: 0, x: -25, y: 0 },
  show: { opacity: 1, x: 0, y: 0 },
}

export default function ListLayoutWithTags({ params: { locale }, posts, title }: ListLayoutProps) {
  const t = useTranslations('blog.home')
  const [currentPage, setCurrentPage] = useState(1)
  const postsPerPage = POSTS_PER_PAGE
  const sortedPosts = sortByDate(posts)
  const setSelectedTag = useTagStore((state) => state.setSelectedTag)

  const filteredPosts = useTagStore((state) => {
    if (state.selectedTag) {
      return sortedPosts.filter((post) => post.tags.includes(state.selectedTag))
    } else {
      return sortedPosts
    }
  })

  const totalPages = Math.ceil(filteredPosts.length / postsPerPage)
  const startIndex = (currentPage - 1) * postsPerPage
  const endIndex = startIndex + postsPerPage
  const displayPosts = filteredPosts.slice(startIndex, endIndex)

  const onPageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleTagClick = (tag: string) => {
    setSelectedTag(tag === useTagStore.getState().selectedTag ? '' : tag)
    setCurrentPage(1)
  }

  const tagCountMap = tagData[locale]

  const TagList = () => (
    <div className="space-y-2">
      {/* <Button
        variant={useTagStore.getState().selectedTag === '' ? "default" : "outline"}
        onClick={() => setSelectedTag('')}
        className="w-full justify-start"
      >
        {t('all')}
      </Button> */}

      <button
                onClick={() => setSelectedTag('')}
                className={`${useTagStore.getState().selectedTag === '' ? 'text-heading-500 dark:text-heading-400' : 'text-primary-500 dark:text-primary-400'} font-bold uppercase`}
              >
                {t('all')}
              </button>
      {Object.entries(tagCountMap).map(([tag, count]) => (
        <Button
          key={tag}
          variant={useTagStore.getState().selectedTag === tag ? "secondary" : "ghost"}
          onClick={() => handleTagClick(tag)}
          className="w-full justify-start"
        >
          {tag} <Badge variant="secondary" className="ml-auto">{count}</Badge>
        </Button>
      ))}
    </div>
  )

  return (
    <div className="container gap-24 flex min-h-screen flex-col flex-1">
      <div>
        <div className="pb-6 pt-6">
          <h1 className="text-3xl font-extrabold leading-9 tracking-tight text-gray-900 dark:text-gray-100 sm:hidden sm:text-4xl sm:leading-10 md:text-6xl md:leading-14">
            {title}
          </h1>
        </div>
        <div className="flex sm:space-x-24">
          <Card 
          className="hidden h-full max-h-screen min-w-[280px] max-w-[280px] flex-wrap overflow-auto  sm:flex"
          >
            <div className="px-6 py-4 w-full">
              <TagList />
            </div>
          </Card>
          <div className="flex-grow">
            <motion.ul variants={container} initial="hidden" animate="show">
              {displayPosts.map((post) => {
                const { slug, date, title, summary, tags, language } = post
                if (language === locale) {
                  return (
                    <motion.li variants={item} key={slug} className="py-5">
                      <article className="space-y-2 xl:grid xl:grid-cols-4 xl:items-baseline xl:space-y-0">
                        <dl>
                          <dt className="sr-only">{t('pub')}</dt>
                          <dd className="text-base font-medium leading-6 text-gray-500 dark:text-gray-400">
                            <time dateTime={date}>{formatDate(date, language)}</time>
                          </dd>
                        </dl>
                        <div className="space-y-3 xl:col-span-3">
                          <div>
                            <h3 className="text-2xl font-bold leading-8 tracking-tight">
                              <Link href={`/${locale}/blog/${slug}`} className="text-gray-900 dark:text-gray-100">
                                {title}
                              </Link>
                            </h3>
                            <div className="flex flex-wrap">
                              {tags.map((tag) => (
                                <Badge
                                  key={tag}
                                  variant={useTagStore.getState().selectedTag === tag ? "default" : "secondary"}
                                  className="mr-2 mt-2 cursor-pointer"
                                  onClick={() => handleTagClick(tag)}
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="prose max-w-none text-gray-500 dark:text-gray-400">
                            {summary!.length > 149 ? `${summary!.substring(0, 149)}...` : summary}
                          </div>
                        </div>
                      </article>
                    </motion.li>
                  )
                }
              })}
            </motion.ul>
            {totalPages > 1 && (
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={onPageChange}
                params={{ locale: locale }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}